/**
 * StarterLandingPage — dedicated `/starter` landing for cold TikTok/Reels traffic.
 *
 * Design intent:
 *   · 60-second video-first hero, single £7 CTA above the fold.
 *   · Zero decision paralysis — one path down the page, one product for sale.
 *   · UTM params (utm_source, utm_medium, utm_campaign) auto-forwarded to the
 *     signup + checkout flow so `/admin/utm-tracking` can attribute purchases
 *     back to the TikTok that drove them.
 *
 * Wiring the hero video:
 *   1. Upload the 60-second cut to YouTube (unlisted is fine).
 *   2. Copy the 11-char video ID (from `youtube.com/watch?v=<ID>`).
 *   3. Paste it into `STARTER_HERO_YOUTUBE_ID` below and redeploy.
 *   Until then the hero shows a styled "coming soon" poster with the CTA still
 *   fully functional — you can start driving traffic before the video is cut.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, PlayCircle, Check, ShieldCheck, Clock, Package, Sparkles, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { StagLogo } from "../components/StagLogo";
import { SiteFooter } from "../components/SiteFooter";
import { SocialMeta } from "../components/SocialMeta";
import { VAULT } from "../data/vaultStats";
import { CheckoutConsentDialog } from "../components/CheckoutConsentDialog";

// Swap this once the 60s hero video is uploaded to YouTube (video ID only).
// Empty string → the hero renders a nicely-styled "coming soon" poster.
const STARTER_HERO_YOUTUBE_ID = "";

const CANONICAL = "https://stagvault.co.uk/starter";

const INCLUSIONS = [
  { icon: Package, title: "AI Prompt Packs", detail: "Bundle 30 niche prompts → sell as a £14 Gumroad download." },
  { icon: Zap, title: "ChatGPT Automation Templates", detail: "Ready-to-sell workflow templates for busy solopreneurs." },
  { icon: Sparkles, title: "AI Resume Builder Service", detail: "Charge £29 per resume, deliver in 4 hours flat." },
  { icon: PlayCircle, title: "Faceless YouTube Scripts", detail: "10-script pack creators pay £47 for — you build it in an hour." },
  { icon: Clock, title: "Social Content Calendar", detail: "£39/month retainer product for TikTok / IG creators." },
];

const FAQS = [
  {
    q: "What am I actually getting for £7?",
    a: "5 copy-paste ChatGPT prompts. Each one outputs a complete product blueprint — positioning, sample content, pricing rationale and a launch checklist. Paste into ChatGPT / Claude / Gemini and go. That's it. No 8-hour course. No upsell tree.",
  },
  {
    q: "How long until I make my money back?",
    a: "Most buyers ship their first product in a weekend. Realistic first-sale price band is £9-£49, so if one of the 5 blueprints turns into a single sale, you've broken even 1-7×. If it doesn't, email us within 14 days and get your £7 back — no questions.",
  },
  {
    q: "Do I need to know how to code or use AI already?",
    a: "No. If you can copy-paste text into a chatbot, you can run every prompt in this pack. The output tells you exactly what to do next.",
  },
  {
    q: "What if I want the full Stag Vault after?",
    a: `You can upgrade to the £25 Lifetime any time. Every future drop is free, forever. ${VAULT.promptsFmt} prompts across ${VAULT.tracks} tracks + unlimited GPT-5.2 runs in the vault.`,
  },
];

export default function StarterLandingPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [consented, setConsented] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const isAuthed = user && typeof user === "object";

  // Track landing view for lightweight attribution reporting.
  useEffect(() => {
    api.post("/analytics/landing-view", {
      page: "/starter",
      utm_source: searchParams.get("utm_source") || null,
      utm_medium: searchParams.get("utm_medium") || null,
      utm_campaign: searchParams.get("utm_campaign") || null,
      referrer: typeof document !== "undefined" ? document.referrer : null,
    }).catch(() => { /* fire-and-forget */ });
  }, [searchParams]);

  const utmSource = (searchParams.get("utm_source") || "").toLowerCase();
  const fromTikTok = utmSource === "tiktok";

  const startStarterCheckout = async () => {
    if (!isAuthed) {
      // Preserve UTM params through signup → back to /starter → the CTA fires again.
      const q = new URLSearchParams(searchParams);
      q.set("next", "/starter");
      q.set("plan", "starter");
      window.location.href = `/initiate?${q.toString()}`;
      return;
    }
    if (!consented) {
      setConsentOpen(true);
      return;
    }
    await launchCheckout();
  };

  const confirmConsent = async () => {
    setConsented(true);
    await launchCheckout();
  };

  const launchCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        origin_url: window.location.origin,
        plan: "starter",
        rewardful_referral: (typeof window !== "undefined" && window.Rewardful && window.Rewardful.referral) || null,
        consent_no_refund: true,
        consent_timestamp: new Date().toISOString(),
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not start checkout. Try again in a moment.");
      setLoading(false);
      setConsentOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="starter-landing-page">
      <SocialMeta
        title="Ship your first AI product this weekend — £7 Starter Pack"
        description="5 copy-paste ChatGPT prompts. Each one outputs a complete AI product blueprint you can sell for £9-£49. Ship your first one this weekend. £7. 14-day no-questions refund."
        canonical={CANONICAL}
        url={CANONICAL}
      />

      {/* Minimal top nav — one CTA, no distraction */}
      <header className="px-6 md:px-12 py-5 flex items-center justify-between border-b border-zinc-900">
        <Link to="/" className="flex items-center gap-3 group" data-testid="starter-landing-home-link">
          <StagLogo className="w-9 h-9" />
          <span className="font-display text-base tracking-tight text-zinc-300 group-hover:text-purple-300 transition-colors">
            Stag Vault
          </span>
        </Link>
        <Link
          to="/sign-in"
          className="text-zinc-400 hover:text-purple-300 text-xs sm:text-sm tracking-[0.2em] uppercase transition-colors"
          data-testid="starter-landing-signin"
        >
          Sign in
        </Link>
      </header>

      {/* HERO — above the fold. Two-column on desktop, stacked on mobile. */}
      <section
        className="relative overflow-hidden border-b border-purple-500/10"
        style={{
          background:
            "radial-gradient(1200px 500px at 20% -10%, rgba(168,85,247,0.14), transparent 60%), radial-gradient(900px 400px at 90% 110%, rgba(176,112,255,0.08), transparent 60%), #050308",
        }}
        data-testid="starter-hero"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16 grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-center">
          {/* Left — copy + CTA */}
          <div>
            {fromTikTok && (
              <div
                className="inline-flex items-center gap-2 mb-6 border border-amber-400/40 bg-amber-500/[0.06] text-amber-200 px-3 py-1.5 text-[11px] tracking-[0.3em] uppercase font-bold"
                data-testid="starter-tiktok-ribbon"
              >
                <Sparkles size={12} /> Welcome from TikTok
              </div>
            )}

            <div className="text-amber-300 text-xs tracking-[0.3em] uppercase mb-5 font-bold inline-flex items-center gap-2">
              <Package size={13} /> The £7 Starter Pack
            </div>

            <h1
              className="font-display text-4xl sm:text-5xl lg:text-[62px] font-black tracking-tighter leading-[1.02] mb-6"
              data-testid="starter-hero-title"
            >
              Ship your first AI product{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(135deg, #ddc4ff 0%, #b070ff 45%, #a855f7 100%)" }}
              >
                this weekend.
              </span>
            </h1>

            <p className="text-zinc-300 text-lg md:text-xl leading-relaxed mb-8 max-w-xl" data-testid="starter-hero-sub">
              5 copy-paste ChatGPT prompts. Each one outputs a complete AI product blueprint you can list on Gumroad tonight and sell for £9-£49 by Sunday.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
              <button
                onClick={startStarterCheckout}
                disabled={loading}
                className="inline-flex items-center gap-3 bg-purple-500 hover:bg-purple-400 disabled:opacity-60 disabled:cursor-not-allowed text-black px-8 py-5 text-sm md:text-base tracking-[0.22em] uppercase font-black transition-all duration-300 shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:shadow-[0_0_55px_rgba(168,85,247,0.85)]"
                data-testid="starter-hero-cta"
              >
                {loading ? (
                  <><Loader2 size={18} className="animate-spin" /> Redirecting to checkout...</>
                ) : (
                  <>Get the Starter Pack · £7 <ArrowRight size={18} /></>
                )}
              </button>
              <span className="text-zinc-500 text-xs tracking-wide">
                One-time · No subscription required · 14-day no-questions refund
              </span>
            </div>

            {/* Consent is collected in the CheckoutConsentDialog when the CTA is clicked. */}
            <CheckoutConsentDialog
              open={consentOpen}
              onOpenChange={(o) => { if (!o && !loading) setConsentOpen(false); }}
              onConfirm={confirmConsent}
              label="Starter Pack"
              price="£7"
              loading={loading}
            />

            {/* Trust strip — 3 hard signals, no fluff */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-zinc-400 text-xs tracking-wide" data-testid="starter-hero-trust">
              <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-purple-400" /> Instant download</span>
              <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-purple-400" /> Works with any AI (GPT, Claude, Gemini)</span>
              <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-purple-400" /> Made by a UK indie</span>
            </div>
          </div>

          {/* Right — video */}
          <div className="w-full">
            <div
              className="relative aspect-video w-full border border-purple-500/25 overflow-hidden bg-[#0a070d] shadow-[0_20px_60px_-20px_rgba(168,85,247,0.35)]"
              data-testid="starter-hero-video"
            >
              {STARTER_HERO_YOUTUBE_ID ? (
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${STARTER_HERO_YOUTUBE_ID}?rel=0&modestbranding=1&playsinline=1`}
                  title="Stag Vault Starter Pack — 60 second overview"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                // Placeholder — beautifully styled, functional even without the real video.
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6"
                     style={{
                       background:
                         "radial-gradient(ellipse at center, rgba(168,85,247,0.12), transparent 65%), linear-gradient(135deg, #0a070d 0%, #12061e 100%)",
                     }}>
                  <div className="w-20 h-20 rounded-full border-2 border-purple-400/60 bg-purple-500/10 flex items-center justify-center backdrop-blur-sm">
                    <PlayCircle size={44} className="text-purple-300" />
                  </div>
                  <div className="text-purple-200 font-display text-lg md:text-xl font-bold tracking-tight">
                    60-second walkthrough
                  </div>
                  <div className="text-zinc-500 text-xs tracking-[0.3em] uppercase">Video · Coming soon</div>
                  <div className="text-zinc-600 text-[11px] tracking-wide max-w-xs">
                    See the 5 prompts running live before you buy.
                  </div>
                </div>
              )}
            </div>
            <div className="mt-3 text-zinc-500 text-xs tracking-wide text-center lg:text-left">
              60 seconds · No sign-up needed to watch
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE — 5 blueprint list */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="starter-inclusions">
        <div className="text-purple-300 text-xs tracking-[0.35em] uppercase mb-4 inline-flex items-center gap-2">
          <span className="w-6 h-px bg-purple-400/60" /> What&apos;s inside
        </div>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-4 leading-[1.05]">
          5 AI products. All copy-paste.
        </h2>
        <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl mb-10">
          Every prompt outputs a complete blueprint: positioning, sample content, pricing rationale, and a launch checklist. Not tips. Not theory. A shippable product.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {INCLUSIONS.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div
                key={idx}
                className="border border-zinc-800 hover:border-purple-500/40 bg-[#0a070d]/60 p-5 md:p-6 flex gap-4 transition-colors"
                data-testid={`starter-inclusion-${idx + 1}`}
              >
                <div className="shrink-0">
                  <span className="font-display text-4xl font-black text-purple-400 leading-none">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={14} className="text-purple-300" />
                    <h3 className="font-display text-base md:text-lg font-bold text-white leading-tight">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed">{item.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* GUARANTEE + secondary CTA */}
      <section
        className="border-y border-purple-500/15"
        style={{
          background:
            "radial-gradient(800px 300px at 50% 0%, rgba(168,85,247,0.08), transparent 60%), #06040a",
        }}
        data-testid="starter-guarantee"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-20 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center">
          <div className="shrink-0 mx-auto md:mx-0">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-amber-400/70 bg-amber-500/[0.08] flex items-center justify-center shadow-[0_0_35px_-10px_rgba(176,112,255,0.6)]">
              <ShieldCheck size={44} className="text-amber-300" />
            </div>
          </div>
          <div className="text-center md:text-left">
            <div className="text-amber-300 text-xs tracking-[0.35em] uppercase mb-3 font-bold">
              14-day no-questions refund
            </div>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-black tracking-tighter mb-4 leading-tight">
              Try it. If it doesn&apos;t click, we refund you. No questions.
            </h3>
            <p className="text-zinc-300 text-base leading-relaxed max-w-2xl mb-6">
              Try the pack. Pick one blueprint. Ship it. If you don&apos;t like it — or you just changed your mind — contact Stag Vault via the <a href="/contact" className="text-purple-300 underline">contact form</a> within 14 days and we&apos;ll refund you in full. No arguments, no forms.
            </p>
            <button
              onClick={startStarterCheckout}
              disabled={loading}
              className="inline-flex items-center gap-3 border border-amber-400 text-amber-200 hover:bg-amber-500 hover:text-black disabled:opacity-60 disabled:cursor-not-allowed px-7 py-3.5 text-sm tracking-[0.22em] uppercase font-bold transition-all"
              data-testid="starter-guarantee-cta"
            >
              Get the pack · £7 <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="starter-faq">
        <div className="text-purple-300 text-xs tracking-[0.35em] uppercase mb-4 inline-flex items-center gap-2">
          <span className="w-6 h-px bg-purple-400/60" /> Fine print, without the fine print
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-10 leading-tight">
          Questions we get most often.
        </h2>
        <div className="space-y-6">
          {FAQS.map((f, idx) => (
            <div key={idx} className="border-l-2 border-purple-500/40 pl-5 md:pl-6" data-testid={`starter-faq-${idx + 1}`}>
              <h3 className="font-display text-base md:text-lg font-bold text-white mb-2">{f.q}</h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="border-t border-purple-500/15"
        style={{
          background: "linear-gradient(135deg, #0a070d 0%, #12061e 100%)",
        }}
        data-testid="starter-final-cta"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
          <div className="text-amber-300 text-xs tracking-[0.35em] uppercase mb-5 font-bold">
            Ready to ship this weekend?
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mb-6 leading-[1.02]">
            One weekend.{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, #ddc4ff 0%, #b070ff 45%, #a855f7 100%)" }}
            >
              One AI product. £7.
            </span>
          </h2>
          <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Cheaper than a Nando&apos;s. 14-day no-questions refund. One button and you&apos;re in.
          </p>
          <button
            onClick={startStarterCheckout}
            disabled={loading}
            className="inline-flex items-center gap-3 bg-purple-500 hover:bg-purple-400 disabled:opacity-60 disabled:cursor-not-allowed text-black px-10 py-5 text-sm md:text-base tracking-[0.22em] uppercase font-black transition-all duration-300 shadow-[0_0_35px_rgba(168,85,247,0.55)] hover:shadow-[0_0_60px_rgba(168,85,247,0.9)]"
            data-testid="starter-final-cta-button"
          >
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Redirecting...</>
            ) : (
              <>Get the Starter Pack · £7 <ArrowRight size={18} /></>
            )}
          </button>
          <div className="mt-6 text-zinc-500 text-xs tracking-wide">
            Or explore the <Link to="/pricing" className="text-purple-300 hover:text-purple-200 underline underline-offset-4">£25 Lifetime</Link> if you want all {VAULT.promptsFmt} prompts →
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
